import type React from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { niceCeil } from '@/lib/playerDashboard';

export type LinePoint = {
  x: number;
  y: number;
  label?: string;
};

export type AccentColor = 'sky' | 'amber' | 'emerald' | 'violet';

interface LineChartProps {
  points: LinePoint[];
  accent?: AccentColor;
  height?: number;
  yFormatter?: (value: number) => string;
  xFormatter?: (value: number) => string;
  yMinHint?: number;
  baseline?: 'zero' | 'auto';
  className?: string;
}

// Monochromatic shadcn palette mirroring the dashboard-react chart tokens
// (`--chart-1` … `--chart-4`). Hardcoded as HSL so the report repo doesn't
// need the CSS-variable theming layer.
const ACCENT_STROKE: Record<AccentColor, string> = {
  sky: 'hsl(222.2, 47.4%, 11.2%)', // chart-1: near-black slate (primary)
  amber: 'hsl(212, 27%, 45%)', // chart-3: slate-blue medium
  emerald: 'hsl(222.2, 47.4%, 11.2%)',
  violet: 'hsl(212, 27%, 45%)',
};

const ACCENT_FILL: Record<AccentColor, string> = {
  sky: 'hsl(222.2, 47.4%, 11.2%)',
  amber: 'hsl(212, 27%, 45%)',
  emerald: 'hsl(222.2, 47.4%, 11.2%)',
  violet: 'hsl(212, 27%, 45%)',
};

const DEFAULT_W = 320;
const PAD_L = 36;
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 22;

const LineChart: React.FC<LineChartProps> = ({
  points,
  accent = 'sky',
  height = 180,
  yFormatter = (v) => v.toFixed(0),
  xFormatter,
  yMinHint = 0,
  baseline = 'zero',
  className = '',
}) => {
  const gradientId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(DEFAULT_W);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Sync the viewBox to the actual rendered pixel width so the SVG never
    // needs preserveAspectRatio="none" stretching — keeps circles round and
    // strokes uniform regardless of card width.
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0)
        setWidth(Math.max(120, Math.round(w)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const layout = useMemo(() => {
    const sorted = [...points]
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
      .sort((a, b) => a.x - b.x);

    if (sorted.length === 0) return null;

    const xs = sorted.map((p) => p.x);
    const ys = sorted.map((p) => p.y);
    const xMin = xs[0];
    const xMax = xs[xs.length - 1];
    const dataMax = Math.max(...ys);
    const dataMin = Math.min(...ys);

    const yMin = baseline === 'zero' ? 0 : Math.min(dataMin, yMinHint);
    const yMaxRaw = Math.max(dataMax, yMinHint, yMin + 1);
    const yMax = niceCeil(yMaxRaw * 1.1);

    const chartW = width - PAD_L - PAD_R;
    const chartH = height - PAD_T - PAD_B;

    const projectX = (x: number) => {
      if (xMax === xMin) return PAD_L + chartW / 2;
      return PAD_L + ((x - xMin) / (xMax - xMin)) * chartW;
    };
    const projectY = (y: number) =>
      PAD_T + (1 - (y - yMin) / (yMax - yMin)) * chartH;

    const projected = sorted.map((p) => ({
      ...p,
      px: projectX(p.x),
      py: projectY(p.y),
    }));

    const linePath = projected
      .map(
        (p, i) =>
          `${i === 0 ? 'M' : 'L'} ${p.px.toFixed(2)} ${p.py.toFixed(2)}`,
      )
      .join(' ');

    const baselineY = projectY(yMin);
    const areaPath =
      projected.length > 1
        ? `${linePath} L ${projected[projected.length - 1].px.toFixed(
            2,
          )} ${baselineY.toFixed(2)} L ${projected[0].px.toFixed(
            2,
          )} ${baselineY.toFixed(2)} Z`
        : null;

    const ticks = [0, 0.5, 1].map((t) => yMin + (yMax - yMin) * t);

    return {
      sorted: projected,
      xMin,
      xMax,
      yMin,
      yMax,
      chartW,
      chartH,
      linePath,
      areaPath,
      baselineY,
      ticks,
    };
  }, [points, height, width, yMinHint, baseline]);

  if (!layout) {
    return (
      <div
        ref={containerRef}
        className={`flex items-center justify-center text-xs text-slate-400 ${className}`}
        style={{ height }}
      >
        —
      </div>
    );
  }

  const stroke = ACCENT_STROKE[accent];
  const fill = ACCENT_FILL[accent];

  return (
    <div ref={containerRef} className={`w-full ${className}`}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        className="block w-full"
        role="img"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={fill} stopOpacity="0.35" />
            <stop offset="100%" stopColor={fill} stopOpacity="0" />
          </linearGradient>
        </defs>

        {layout.ticks.map((t, i) => {
          const y =
            PAD_T +
            (1 - (t - layout.yMin) / (layout.yMax - layout.yMin)) *
              layout.chartH;
          return (
            <g key={i}>
              <line
                x1={PAD_L}
                x2={width - PAD_R}
                y1={y}
                y2={y}
                stroke="#e2e8f0"
                strokeWidth={1}
                strokeDasharray={i === 0 ? '0' : '2 3'}
              />
              <text
                x={PAD_L - 6}
                y={y + 3}
                textAnchor="end"
                fontSize="9"
                fill="#94a3b8"
                fontFamily="ui-sans-serif, system-ui, sans-serif"
              >
                {yFormatter(t)}
              </text>
            </g>
          );
        })}

        {layout.areaPath && (
          <path
            d={layout.areaPath}
            fill={`url(#${gradientId})`}
            stroke="none"
          />
        )}

        <path
          d={layout.linePath}
          fill="none"
          stroke={stroke}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {layout.sorted.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.px}
              cy={p.py}
              r={3.2}
              fill="white"
              stroke={stroke}
              strokeWidth={1.6}
            />
            {p.label && (
              <title>
                {p.label}: {yFormatter(p.y)}
              </title>
            )}
          </g>
        ))}

        {xFormatter && (
          <>
            <text
              x={PAD_L}
              y={height - 6}
              textAnchor="start"
              fontSize="9"
              fill="#94a3b8"
              fontFamily="ui-sans-serif, system-ui, sans-serif"
            >
              {xFormatter(layout.xMin)}
            </text>
            {layout.xMax !== layout.xMin && (
              <text
                x={width - PAD_R}
                y={height - 6}
                textAnchor="end"
                fontSize="9"
                fill="#94a3b8"
                fontFamily="ui-sans-serif, system-ui, sans-serif"
              >
                {xFormatter(layout.xMax)}
              </text>
            )}
          </>
        )}
      </svg>
    </div>
  );
};

export default LineChart;
